import { Input } from '@/components/common/Input';
import type { DraftAccountUsage } from './types';

interface Props {
  value: DraftAccountUsage;
  onChange: (next: DraftAccountUsage) => void;
}

const FIELD_ROW = 'grid grid-cols-1 gap-4 md:grid-cols-2';
const LABEL = 'mb-1 block text-xs font-medium text-[#555]';

function parseNumber(raw: string): number | undefined {
  if (raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function AccountUsagePanel({ value, onChange }: Props) {
  const set = <K extends keyof DraftAccountUsage>(key: K, v: DraftAccountUsage[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#333]">Estimated transaction volume</h3>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Incoming volume (EUR / mo)</label>
            <Input
              type="number"
              value={value.estimatedIncomingVolume?.toString() ?? ''}
              onChange={(v) => set('estimatedIncomingVolume', parseNumber(v))}
            />
          </div>
          <div>
            <label className={LABEL}>Incoming transactions / mo</label>
            <Input
              type="number"
              value={value.estimatedIncomingTxCount?.toString() ?? ''}
              onChange={(v) => set('estimatedIncomingTxCount', parseNumber(v))}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Outgoing volume (EUR / mo)</label>
            <Input
              type="number"
              value={value.estimatedOutgoingVolume?.toString() ?? ''}
              onChange={(v) => set('estimatedOutgoingVolume', parseNumber(v))}
            />
          </div>
          <div>
            <label className={LABEL}>Outgoing transactions / mo</label>
            <Input
              type="number"
              value={value.estimatedOutgoingTxCount?.toString() ?? ''}
              onChange={(v) => set('estimatedOutgoingTxCount', parseNumber(v))}
            />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#333]">Account usage</h3>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Source of income</label>
            <Input
              value={value.sourceOfIncome ?? ''}
              onChange={(v) => set('sourceOfIncome', v)}
              placeholder="Corporate revenue, fundraising…"
            />
          </div>
          <div>
            <label className={LABEL}>Monthly income (EUR)</label>
            <Input
              type="number"
              value={value.monthlyIncome?.toString() ?? ''}
              onChange={(v) => set('monthlyIncome', parseNumber(v))}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Purpose of account</label>
            <Input
              value={value.purposeOfAccount ?? ''}
              onChange={(v) => set('purposeOfAccount', v)}
            />
          </div>
          <div>
            <label className={LABEL}>Intended use</label>
            <Input value={value.intendedUse ?? ''} onChange={(v) => set('intendedUse', v)} />
          </div>
        </div>
      </section>
    </div>
  );
}
